import React, { useRef } from 'react';
import _ from 'lodash';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import appConfig from 'app-config';
import PolicySummaryVehicleInfoComponent from '../PolicySummaryVehicleInfoComponent/PolicySummaryVehicleInfoComponent';
import metadata from './PolicySummaryVehiclesComponent.metadata.json5';
import messages from './PolicySummaryVehiclesComponent.messages';
import styles from './PolicySummaryVehiclesComponent.module.scss';
import PolicySummaryRentedTDCComponent from '../PolicySummaryRentedTDCComponent/PolicySummaryRentedTDCComponent';

function PolicySummaryVehiclesComponent(props) {
    const {
        model: vehicles,
        isNamedNonOwner,
        coverages,
        termType,
        editVehicles,
        editCoverages,
        gpaMode,
        isPolicyChange,
        isModernProduct
    } = props;
    const translator = useTranslator();
    const isQnB = appConfig.portalCode === 'QB';
    const displayedLineCoverages = useRef(new Map());

    const renderEditVehiclesAction = () => {
        if(!gpaMode && isPolicyChange && !isQnB) {
            return (
                <a className={styles.editVehiclesLink} onClick={editVehicles}>
                    {translator(messages.summaryEditVehicles)}
                </a>
            );
        }
        return '';
    };

    const renderVehicleTitle = () => {
        return (
            <h2 className="subsection-header">
                {translator(messages.summaryVehicles)}
            </h2>
        );
    };

    const getYearCellHeader = () => {
        return (
            <>
                <span className={styles.standardHeader}>{ translator(messages.summaryVehicleYear) }</span>
                <span className={styles.mobileHeader}>{ _.capitalize(translator(messages.summaryVehicleYear)) }:</span>
            </>
        )
    };

    const getMakeCellHeader = () => {
        return (
            <>
                <span className={styles.standardHeader}>{ translator(messages.summaryVehicleMake) }</span>
                <span className={styles.mobileHeader}>{ _.capitalize(translator(messages.summaryVehicleMake)) }:</span>
            </>
        )
    };

    const getModelCellHeader = () => {
        return (
            <>
                <span className={styles.standardHeader}>{ translator(messages.summaryVehicleModel) }</span>
                <span className={styles.mobileHeader}>{ _.capitalize(translator(messages.summaryVehicleModel)) }:</span>
            </>
        )
    };

    const getTrimCellHeader = () => {
        return (
            <>
                <span className={styles.standardHeader}>{ translator(messages.summaryVehicleTrim) }</span>
                <span className={styles.mobileHeader}>{ _.capitalize(translator(messages.summaryVehicleTrim)) }:</span>
            </>
        )
    };

    const getVinCellHeader = () => {
        return (
            <>
                <span className={styles.standardHeader}>{ translator(messages.summaryVehicleVin) }</span>
                <span className={styles.mobileHeader}>{ _.capitalize(translator(messages.summaryVehicleVin)) }:</span>
            </>
        )
    };

    const getYearCell = (item) => {
        return <span className={ styles.vehicleBoldCellValue }>{ item.year.value }</span>;
    };

    const getMakeCell = (item) => {
        return <span className={ styles.vehicleBoldCellValue }>{ item.make.value }</span>;
    };

    const getModelCell = (item) => {
        return <span className={ styles.vehicleCellValue }>{ item.model.value }</span>;
    };

    const getTrimCell = (item) => {
        return <span className={ styles.vehicleCellValue }>{ item.trim_ML.value }</span>;
    };

    const getVinCell = (item) => {
        return <span className={ styles.vehicleBoldCellValue }>{ item.vin.value }</span>;
    };

    const renderCoveragesTitle = () => {
        if (vehicles.length > 1) {
            return translator(messages.summaryCoverages);
        }
        return translator(messages.summaryVehicleCoverages);
    };

    const generateOverrides = () => {
        const overrides = vehicles.map((vehicle, index) => {
            return {
                [`policySummaryVehicleInfoComponent${index}`]: {
                    model: vehicle,
                    index,
                    isNamedNonOwner,
                    coverages,
                    termType,
                    editCoverages,
                    isPolicyChange,
                    gpaMode,
                    displayedLineCoverages: displayedLineCoverages.current,
                    isModernProduct
                }
            };
        });

        return Object.assign({}, ...overrides);
    };
    const rentedTDCList = vehicles
        .map(v => v?.rentedTDCVehicles_ML)
        .filter(item =>
            item &&
            (
                item.make ||
                item.model ||
                item.serial_ML ||
                item.year
            )
        );

    const overrideProps = {
        vehiclesTableContainer: {
            visible: isNamedNonOwner === false
        },
        vehiclesTable: {
            data: vehicles,
            visible: isNamedNonOwner === false,
            renderTitle: renderVehicleTitle,
            renderTitleAction: renderEditVehiclesAction
        },
        vehicleCoverages: {
            content: renderCoveragesTitle(),
            visible: vehicles.length > 0
        },
        vehiclesDetailsIterable: {
            data: vehicles
        },
        paRentedTDCComponent: {
            data: vehicles,
            visible: rentedTDCList.length > 0
        },
        ...generateOverrides()
    };


    const resolvers = {
        resolveCallbackMap: {
            getYearCellHeader: getYearCellHeader,
            getMakeCellHeader: getMakeCellHeader,
            getModelCellHeader: getModelCellHeader,
            getTrimCellHeader: getTrimCellHeader,
            getVinCellHeader: getVinCellHeader,
            getYearCell: getYearCell,
            getMakeCell: getMakeCell,
            getModelCell: getModelCell,
            getTrimCell: getTrimCell,
            getVinCell: getVinCell
        },
        resolveComponentMap: {
            policySummaryVehicleInfoComponent: PolicySummaryVehicleInfoComponent,
            rentedTDC: PolicySummaryRentedTDCComponent
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            model={vehicles}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={styles}
        />
    );
}

PolicySummaryVehiclesComponent.propTypes = {
    model: PropTypes.shape([]).isRequired,
    coverages: PropTypes.shape({}).isRequired,
    isNamedNonOwner: PropTypes.bool.isRequired,
    termType: PropTypes.string.isRequired,
    editVehicles: PropTypes.func.isRequired,
    editCoverages: PropTypes.func.isRequired,
    gpaMode:PropTypes.bool,
    isPolicyChange: PropTypes.bool,
    vehicles: PropTypes.shape([]).isRequired,
};
PolicySummaryVehiclesComponent.defaultProps = {
    gpaMode: false,
    isPolicyChange: true,
    vehicles: []
};

export default PolicySummaryVehiclesComponent;